import { useStepsContext } from '@/providers/pi-multi-step-form/StepsContext';
import StepContainer from './step-container';
import Alert from '@/components/pages/alert';
import Image from 'next/image';
import { clsx } from 'clsx';
import { Check, Person } from '@/components/pages/icons';

const inputClassNames =
  'w-full bg-white ring-inset ring-1 ring-black/20 rounded-lg p-4 py-3 text-brand-default focus:ring-brand-accent focus:outline-none text-base leading-6 box-border border-0 focus:shadow-brand-input transition-all duration-300';

export default function TellUsAboutYourself() {
  const {
    submitLead,
    isFetching,
    register,
    errors,
    phoneNumberValue,
    zipCodeValue,
    onPhoneNumberChange,
    onPhoneNumberBlur,
    onZipCodeBlur,
    trigger,
    errorClassNames,
    nextStep,
    getValues,
    consent,
  } = useStepsContext();

  const onContinue = async () => {
    const isValid = await trigger();
    if (isValid) {
      submitLead(getValues());
      nextStep();
    }
  };

  const isValid =
    !errors?.lead?.contact?.first_name &&
    !errors?.lead?.contact?.last_name &&
    !errors?.lead?.contact?.phone &&
    !errors?.lead?.contact?.zip_code &&
    !errors?.lead?.contact?.email &&
    !errors?.lead?.contact?.consent;

  return (
    <StepContainer
      title="Tell us about yourself"
      subtitle="Let us know how we can reach you if we need additional details."
      onSubmit={onContinue}
      buttonDisabled={!isValid || isFetching}
      buttonLabel="Start free review"
    >
      <>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            placeholder="First name"
            {...register('lead.contact.first_name')}
            className={clsx(inputClassNames, errors?.lead?.contact?.first_name && errorClassNames)}
            onBlur={() => trigger('lead.contact.first_name')}
          />
          <input
            type="text"
            placeholder="Last name"
            {...register('lead.contact.last_name')}
            className={clsx(inputClassNames, errors?.lead?.contact?.last_name && errorClassNames)}
            onBlur={() => trigger('lead.contact.last_name')}
          />
          <input
            type="text"
            placeholder="Phone number"
            value={phoneNumberValue}
            onChange={onPhoneNumberChange}
            onBlur={onPhoneNumberBlur}
            className={clsx(inputClassNames, errors?.lead?.contact?.phone && errorClassNames)}
            onKeyUp={() => trigger('lead.contact.phone')}
          />
          <input
            type="text"
            placeholder="ZIP code"
            {...register?.('lead.contact.zip_code')}
            className={clsx(inputClassNames, errors?.lead?.contact?.zip_code && errorClassNames)}
            onBlur={onZipCodeBlur}
            value={zipCodeValue}
            onKeyUp={() => trigger('lead.contact.zip_code')}
          />
          <input
            type="email"
            placeholder="Email"
            {...register('lead.contact.email')}
            className={clsx(
              inputClassNames,
              'col-span-2',
              errors?.lead?.contact?.email && errorClassNames
            )}
            onBlur={() => trigger('lead.contact.email')}
          />
        </div>

        <div className="group flex items-start gap-2 col-span-2 cursor-pointer mb-8">
          <input
            type="checkbox"
            className="sr-only"
            id="form-consent-input"
            {...register?.('lead.contact.consent')}
            onChange={async (e) => {
              await register('lead.contact.consent').onChange(e);
              await trigger('lead.contact.consent');
            }}
          />
          <label
            htmlFor="form-consent-input"
            className={clsx(
              'cursor-pointer ring ring-[#D9D9D9] ring-inset bg-white group-has-checked:bg-brand-button min-w-5.5 h-5.5 flex items-center justify-center rounded-lg transition-colors',
              errors?.lead?.contact?.consent && errorClassNames
            )}
          >
            <Check className="size-3.5 text-white opacity-0 group-has-checked:opacity-100 transition-opacity" />
          </label>

          <label
            htmlFor="form-consent-input"
            className={clsx(
              'cursor-pointer grow text-xs font-medium leading-normal tracking-[0.18px] text-[#6B7280] [&_a]:hover:text-brand-button',
              errors?.lead?.contact?.consent && errorClassNames
            )}
            dangerouslySetInnerHTML={{ __html: consent }}
          />
        </div>

        <Alert icon={<Person />}>
          <p>Talk to a real, experienced case specialist who will review your case in minutes.</p>
          <div className="flex items-center gap-2">
            <Image
              src="/images/maria.webp"
              alt="Maria"
              width={84}
              height={84}
              className="rounded-lg w-21 h-21"
            />
            <p>
              <b>Maria, case specialist</b> <br />
              Houston, TX
            </p>
          </div>
        </Alert>
      </>
    </StepContainer>
  );
}
